import { useState } from 'react';
import { CalendarClock, Gauge, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, ConfirmationModal, EmptyState, Skeleton } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import {
  CLIENT_QUOTAS_QUERY,
  DELETE_CLIENT_QUOTA_MUTATION,
  DELETE_QUOTA_MUTATION,
  QUOTAS_QUERY,
  UPSERT_CLIENT_QUOTA_MUTATION,
  UPSERT_QUOTA_MUTATION,
  type ClientQuotasQueryResult,
  type DeleteClientQuotaMutationResult,
  type DeleteClientQuotaMutationVariables,
  type DeleteQuotaMutationResult,
  type DeleteQuotaMutationVariables,
  type QuotasQueryResult,
  type UpsertClientQuotaMutationResult,
  type UpsertClientQuotaMutationVariables,
  type UpsertQuotaMutationResult,
  type UpsertQuotaMutationVariables,
} from '../gql/operations';
import {
  QuotaEnforcementMode,
  type ClientQuota,
  type Quota,
  type UpsertClientQuotaInput,
  type UpsertQuotaInput,
} from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { QuotaScopeBadge } from '../features/shared/StatusBadge';
import { InlineBanner } from '../features/shared/InlineBanner';
import { QuotaFormModal } from '../features/quotas/QuotaFormModal';
import { ClientQuotaFormModal } from '../features/quotas/ClientQuotaFormModal';

// Quota editor (US-8.4.1). Lists rate/capacity quotas across scopes, with create/edit (modal)
// and confirm-gated delete. Surfaces the most-specific-scope precedence so operators understand
// which limit wins when several scopes match the same request.

function LimitChip({ label, value }: { label: string; value: number | null | undefined }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const bounded = value != null;
  return (
    <div
      className={`rounded-xl border px-3 py-2 ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <span
        className={`block text-[10px] font-medium uppercase tracking-widest ${
          isDark ? 'text-white/30' : 'text-slate-400'
        }`}
      >
        {label}
      </span>
      <span
        className={`tabular-nums text-sm font-semibold ${
          bounded
            ? isDark
              ? 'text-white/85'
              : 'text-slate-800'
            : isDark
              ? 'text-white/30'
              : 'text-slate-400'
        }`}
      >
        {bounded ? value.toLocaleString('en-US') : '∞'}
      </span>
    </div>
  );
}

function QuotaCard({
  quota,
  onEdit,
  onDelete,
}: {
  quota: Quota;
  onEdit: (quota: Quota) => void;
  onDelete: (quota: Quota) => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <article
      aria-label={`Quota ${quota.scopeName ?? quota.scopeId}`}
      className={`rounded-2xl border p-5 transition-all duration-300 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/20">
            <Gauge className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <QuotaScopeBadge scope={quota.scope} />
              {quota.scopeName ? (
                <span
                  className={`truncate text-sm font-semibold ${
                    isDark ? 'text-white' : 'text-slate-900'
                  }`}
                  title={quota.scopeName}
                >
                  {quota.scopeName}
                </span>
              ) : (
                <span
                  className={`text-xs italic ${
                    isDark ? 'text-white/40' : 'text-slate-400'
                  }`}
                >
                  (orphaned — no matching entity)
                </span>
              )}
            </div>
            {/* The id stays visible as a small mono subtitle so an operator can still
                grep audit logs / GraphQL traces by it, but it no longer dominates the
                card's primary identity line. */}
            <code
              className={`mt-1 block truncate font-mono text-[11px] ${
                isDark ? 'text-white/40' : 'text-slate-400'
              }`}
              title={quota.scopeId}
            >
              {quota.scopeId}
            </code>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onEdit(quota)}
            leftIcon={<Pencil className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Edit quota ${quota.scopeName ?? quota.scopeId}`}
          >
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(quota)}
            leftIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Delete quota ${quota.scopeName ?? quota.scopeId}`}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <LimitChip label="RPM" value={quota.rpm} />
        <LimitChip label="TPM" value={quota.tpm} />
        <LimitChip label="Daily cap" value={quota.dailyTokenCap} />
        <LimitChip label="Max conc." value={quota.maxConcurrency} />
      </div>
    </article>
  );
}

export function EnforcementModeBadge({ mode }: { mode: QuotaEnforcementMode }) {
  const isBlock = mode === QuotaEnforcementMode.Block;
  return (
    <span
      data-testid="enforcement-mode"
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
        isBlock
          ? 'bg-rose-500/15 text-rose-500'
          : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
      }`}
    >
      {isBlock ? 'Block' : 'Warn'}
    </span>
  );
}

function ClientQuotaCard({
  quota,
  onEdit,
  onDelete,
}: {
  quota: ClientQuota;
  onEdit: (quota: ClientQuota) => void;
  onDelete: (quota: ClientQuota) => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const name = quota.clientName ?? quota.clientId;

  return (
    <article
      aria-label={`Monthly quota ${name}`}
      className={`rounded-2xl border p-5 transition-all duration-300 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20">
            <CalendarClock className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={`truncate text-sm font-semibold ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
                title={name}
              >
                {name}
              </span>
              <EnforcementModeBadge mode={quota.enforcementMode} />
            </div>
            <code
              className={`mt-1 block truncate font-mono text-[11px] ${
                isDark ? 'text-white/40' : 'text-slate-400'
              }`}
              title={quota.clientId}
            >
              {quota.clientId}
            </code>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onEdit(quota)}
            leftIcon={<Pencil className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Edit monthly quota ${name}`}
          >
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(quota)}
            leftIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Delete monthly quota ${name}`}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <LimitChip label="Tokens / month" value={quota.monthlyTokenCap} />
        <LimitChip label="Requests / month" value={quota.monthlyRequestCap} />
      </div>
    </article>
  );
}

export function QuotaEditorPage() {
  const { data, loading, error, refetch } = useQuery<QuotasQueryResult>(QUOTAS_QUERY);
  const upsert = useMutation<UpsertQuotaMutationResult, UpsertQuotaMutationVariables>(
    UPSERT_QUOTA_MUTATION,
  );
  const remove = useMutation<DeleteQuotaMutationResult, DeleteQuotaMutationVariables>(
    DELETE_QUOTA_MUTATION,
  );

  // Client MONTHLY quotas (2026-08-09) — a separate section on this page.
  const clientQuotasQuery = useQuery<ClientQuotasQueryResult>(CLIENT_QUOTAS_QUERY);

  const upsertClientQuota = useMutation<
    UpsertClientQuotaMutationResult,
    UpsertClientQuotaMutationVariables
  >(UPSERT_CLIENT_QUOTA_MUTATION);

  const removeClientQuota = useMutation<
    DeleteClientQuotaMutationResult,
    DeleteClientQuotaMutationVariables
  >(DELETE_CLIENT_QUOTA_MUTATION);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Quota | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Quota | null>(null);

  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [editingClientQuota, setEditingClientQuota] = useState<ClientQuota | null>(null);
  const [clientDeleteTarget, setClientDeleteTarget] = useState<ClientQuota | null>(null);

  const quotas = data?.quotas ?? [];
  const clientQuotas = clientQuotasQuery.data?.clientQuotas ?? [];

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (quota: Quota) => {
    setEditing(quota);
    setFormOpen(true);
  };

  const handleSubmit = async (input: UpsertQuotaInput) => {
    try {
      await upsert.mutate({ input });
      setFormOpen(false);
      setEditing(null);
      refetch();
    } catch {
      // Surfaced via upsert.error banner; keep the form open for retry.
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove.mutate({ id: deleteTarget.id });
      setDeleteTarget(null);
      refetch();
    } catch {
      // Surfaced via remove.error banner; keep the confirm dialog open.
    }
  };

  const openClientCreate = () => {
    setEditingClientQuota(null);
    setClientFormOpen(true);
  };
  const openClientEdit = (quota: ClientQuota) => {
    setEditingClientQuota(quota);
    setClientFormOpen(true);
  };

  const handleClientSubmit = async (input: UpsertClientQuotaInput) => {
    try {
      await upsertClientQuota.mutate({ input });
      setClientFormOpen(false);
      setEditingClientQuota(null);
      clientQuotasQuery.refetch();
    } catch {
      // Surfaced via upsertClientQuota.error banner; keep the form open for retry.
    }
  };

  const handleClientConfirmDelete = async () => {
    if (!clientDeleteTarget) return;
    try {
      await removeClientQuota.mutate({ id: clientDeleteTarget.id });
      setClientDeleteTarget(null);
      clientQuotasQuery.refetch();
    } catch {
      // Surfaced via removeClientQuota.error banner; keep the confirm dialog open.
    }
  };

  const mutationError =
    upsert.error ?? remove.error ?? upsertClientQuota.error ?? removeClientQuota.error ?? null;

  return (
    <div>
      <PageHeader
        title="Quotas"
        description="Rate and capacity limits applied per key, application, or client."
        breadcrumbs={[{ label: 'Providers', to: '/providers' }, { label: 'Quotas' }]}
        actions={
          <Button
            variant="primary"
            onClick={openCreate}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            New quota
          </Button>
        }
      />

      {/* Precedence guidance — most-specific scope wins (US-8.4.1 copy). */}
      <InlineBanner
        tone="info"
        role="status"
        icon={<ShieldCheck className="h-4 w-4" aria-hidden="true" />}
      >
        When several quotas could apply to one request, the most specific scope wins:{' '}
        <strong>Key &gt; App &gt; Client</strong>. A key-level limit overrides an app-level one,
        which overrides a client-level one.
      </InlineBanner>

      {error && (
        <InlineBanner tone="error">Couldn’t load quotas: {error.message}</InlineBanner>
      )}

      {mutationError && (
        <InlineBanner tone="error" testId="mutation-error">
          That action couldn’t be completed: {mutationError.message}
        </InlineBanner>
      )}

      {loading && (
        <div className="space-y-4" data-testid="quotas-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="block" height={130} />
          ))}
        </div>
      )}

      {!loading && !error && quotas.length === 0 && (
        <EmptyState
          icon={<Gauge className="h-16 w-16" aria-hidden="true" />}
          title="No quotas defined"
          description="Add a quota to cap request rate, token throughput, or concurrency for a key, app, or client."
          action={{ label: 'Create first quota', onClick: openCreate }}
        />
      )}

      {!loading && quotas.length > 0 && (
        <ul className="space-y-4">
          {quotas.map((quota) => (
            <li key={quota.id}>
              <QuotaCard quota={quota} onEdit={openEdit} onDelete={setDeleteTarget} />
            </li>
          ))}
        </ul>
      )}

      {/* ── Client monthly budgets (2026-08-09) ─────────────────────────────── */}
      <section aria-labelledby="monthly-quotas-heading" className="mt-10">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2
            id="monthly-quotas-heading"
            className="text-lg font-semibold tracking-tight"
          >
            Monthly budgets
          </h2>
          <Button
            variant="primary"
            onClick={openClientCreate}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            New monthly quota
          </Button>
        </div>

        <InlineBanner
          tone="info"
          role="status"
          icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
        >
          A per-client budget for the calendar month (UTC), in tokens and requests.{' '}
          <strong>Warn</strong> (default) lets traffic continue past the cap and raises a
          dashboard alert; <strong>Block</strong> rejects requests until the month rolls over.
          Track progress on the{' '}
          <a href="/reports/quotas" className="font-semibold underline underline-offset-2">
            monthly report
          </a>
          .
        </InlineBanner>

        {clientQuotasQuery.error && (
          <InlineBanner tone="error">
            Couldn’t load monthly quotas: {clientQuotasQuery.error.message}
          </InlineBanner>
        )}

        {clientQuotasQuery.loading && (
          <div className="space-y-4" data-testid="client-quotas-loading">
            <Skeleton variant="block" height={130} />
          </div>
        )}

        {!clientQuotasQuery.loading &&
          !clientQuotasQuery.error &&
          clientQuotas.length === 0 && (
            <EmptyState
              icon={<CalendarClock className="h-16 w-16" aria-hidden="true" />}
              title="No monthly budgets defined"
              description="Add a monthly quota to give a client a tokens-per-month or requests-per-month budget, warned or hard-enforced."
              action={{ label: 'Create first monthly quota', onClick: openClientCreate }}
            />
          )}

        {!clientQuotasQuery.loading && clientQuotas.length > 0 && (
          <ul className="space-y-4">
            {clientQuotas.map((quota) => (
              <li key={quota.id}>
                <ClientQuotaCard
                  quota={quota}
                  onEdit={openClientEdit}
                  onDelete={setClientDeleteTarget}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <QuotaFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        quota={editing}
        loading={upsert.loading}
        onSubmit={(input) => void handleSubmit(input)}
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleConfirmDelete()}
        variant="danger"
        title="Delete this quota?"
        confirmLabel="Delete quota"
        loading={remove.loading}
        description={
          <>
            Deleting the quota for <code className="font-mono">{deleteTarget?.scopeId}</code>{' '}
            removes its limits immediately. This cannot be undone.
          </>
        }
      />

      <ClientQuotaFormModal
        isOpen={clientFormOpen}
        onClose={() => {
          setClientFormOpen(false);
          setEditingClientQuota(null);
        }}
        quota={editingClientQuota}
        takenClientIds={clientQuotas.map((q) => q.clientId)}
        loading={upsertClientQuota.loading}
        onSubmit={(input) => void handleClientSubmit(input)}
      />

      <ConfirmationModal
        isOpen={clientDeleteTarget !== null}
        onClose={() => setClientDeleteTarget(null)}
        onConfirm={() => void handleClientConfirmDelete()}
        variant="danger"
        title="Delete this monthly quota?"
        confirmLabel="Delete monthly quota"
        loading={removeClientQuota.loading}
        description={
          <>
            Deleting the monthly quota for{' '}
            <strong>{clientDeleteTarget?.clientName ?? clientDeleteTarget?.clientId}</strong>{' '}
            removes its budget immediately — traffic is no longer warned or blocked. Usage
            history is kept. This cannot be undone.
          </>
        }
      />
    </div>
  );
}
